import React from "react";
import styled from "@emotion/styled";
import LightbulbIcon from "../images/LightbulbIcon";
import { logout } from "../store/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { hideNavigation } from "../store/uiSlice";
import { RootState } from "../store";
import imgInKindLogoNewJon from "../images/imgInKindLogoNewJon.png";

const Backdrop = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? "auto" : "none")};
  transition: opacity 0.3s ease;
  z-index: 999;
`;

const NavBarContainer = styled.div<{ visible: boolean }>`
  width: 414px;
  min-height: 100dvh;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 0 0 16px 16px;
  margin: 0 auto;
  z-index: 1000;
  transform: translateX(${({ visible }) => (visible ? "0" : "-100%")});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    transform: translateX(
      ${({ visible }) => (visible ? "max(12.5vw, 0px)" : "-100vw")}
    );
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const NavbarContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 376px;
  background: #0f0f0f;
  min-height: 100dvh;
  border-radius: 0 0 16px 16px;
  width: 90vw;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 90%;
  margin: 0 auto;
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 0 33px;
`;

const Logo = styled.img`
  margin: 20px 0;
  width: 76px;
  height: 52px;
  object-fit: contain;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 36px;
  width: 100%;
`;

const UserInfoText = styled.div`
  color: #fff;
  font-family: "Rethink-Sans-Bold", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const UserInfoText2 = styled.div`
  color: #fff;
  font-family: "Rethink-Sans-Regular", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const Divider = styled.img`
  width: 100%;
  max-width: 376px;
  height: 1px;
  margin: 0 auto;
  margin-top: 16px;
  margin-bottom: 8px;
  background: #353535;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 48px;
  left: 34px;
  width: 100%;
  gap: 42px;
`;

const Button = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #fff;
  font-family: "Rethink-Sans-Bold", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
`;

const NavLink = styled.a`
  text-decoration: none;
  color: inherit;
  &:hover,
  &:active {
    text-decoration: none;
  }
`;

export default function NavigationBar() {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const visible = useSelector((state: RootState) => state.ui.navigationVisible);
  return (
    <>
      <Backdrop visible={visible} />
      <NavBarContainer
        visible={visible}
        className={visible ? "mwa-nav-bar-container" : ""}
      >
        <NavbarContent>
          <HeaderRow>
            <Logo src={imgInKindLogoNewJon} alt="inKind Logo" />
            <button onClick={() => dispatch(hideNavigation())}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M16.6201 21.0099C16.1301 21.4999 15.3401 21.4999 14.8501 21.0099L6.54006 12.6999C6.15006 12.3099 6.15006 11.6799 6.54006 11.2899L14.8501 2.97994C15.3401 2.48994 16.1301 2.48994 16.6201 2.97994C17.1101 3.46994 17.1101 4.25994 16.6201 4.74994L9.38006 11.9999L16.6301 19.2499C17.1101 19.7299 17.1101 20.5299 16.6201 21.0099Z"
                  fill="white"
                />
              </svg>
            </button>
          </HeaderRow>
          <Divider />
          <UserInfo>
            <UserInfoText>{`${user?.first_name} ${user?.last_name}`}</UserInfoText>
            <UserInfoText2>{user?.email}</UserInfoText2>
          </UserInfo>
          <Divider />
          <ButtonContainer>
            <NavLink href="mailto:partner.support@inkind.com">
              <Button as="span">
                <LightbulbIcon />
                <span>Contact Support</span>
              </Button>
            </NavLink>
            <NavLink
              href="https://resourcehub.inkind.com/managers-training"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button as="span">
                <LightbulbIcon />
                <span>Manager’s Training</span>
              </Button>
            </NavLink>
            <Button onClick={() => dispatch(logout())}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M14 4.44829V0.999874C14 0.450883 13.5615 0 13.0232 0H0.976839C0.440495 0 0 0.448795 0 0.999874V1.64906V14.6787V15.1296C0 15.497 0.21209 15.8456 0.520029 16.0126L7.94319 19.9453C8.26948 20.1123 8.65899 19.8785 8.65899 19.4944V15.6619H13.0211C13.5575 15.6619 13.998 15.2131 13.998 14.662V9.19717H12.0443V13.1633C12.0443 13.4472 11.8322 13.6622 11.5569 13.6622H8.67531V8.53128V5.28117V5.18098C8.67531 4.81359 8.46322 4.46499 8.15528 4.298L3.77684 1.99975H11.5569C11.8342 1.99975 12.0443 2.21684 12.0443 2.49864V4.46499H13.998V4.44829H14Z"
                  fill="#F3B900"
                />
                <path
                  d="M16.8718 3.30126L19.725 6.32551C20.0917 6.71423 20.0917 7.2876 19.725 7.67437L16.8718 10.7006C16.505 11.0893 15.9494 11.1048 15.5845 10.7161C15.2325 10.3429 15.2765 9.72293 15.6139 9.35171L16.9011 8.00284H11.9099C11.6605 8.00284 11.413 7.894 11.2516 7.69187C10.8427 7.22734 10.9436 6.40326 11.4716 6.09422C11.6037 6.01648 11.765 5.96983 11.9099 5.96983H16.9011C16.9011 5.96983 15.6286 4.62097 15.6139 4.62097C15.2765 4.26335 15.2325 3.62779 15.5845 3.27211C15.9348 2.89699 16.505 2.91254 16.8718 3.30126Z"
                  fill="#F3B900"
                />
              </svg>
              <span>Logout</span>
            </Button>
          </ButtonContainer>
        </NavbarContent>
      </NavBarContainer>
    </>
  );
}
